import React from 'react';
import { Modal, Button, Form, Row, Col, Alert, Card, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import Component from '../../components/AscComponent';
import { isValidExternalNumber } from '../../components/AscUtils';
import { CSVLink } from "react-csv";
import { CSVReader } from 'react-papaparse';
import '../Elements/AscElements/PhoneBookElements.css';
import '../Elements/Modal/Modal.css';

/**
 * 【#607】インポート結果のエラーから、除外された電話番号の数（重複を除く）を数える。
 * 電話番号の有効判定は手動登録と同じ isValidExternalNumber（数字のみ・3〜128桁）で行う。
 * 判定を 3〜128 桁に広げたため、名前・件数・行番号などの数字を番号として拾わないよう、
 * エラーの種別と出どころごとに番号の取り出し方を決めている。
 *
 * - クライアントの形式不正（invalid_format）: value に入力どおりの番号が入る。形式不正でも
 *   除外された番号なので、判定にかけずにそのまま数える
 * - クライアントの名前・電話番号の欠落（missing_name / missing_phone）: 番号ではないので数えない
 *   （value には名前が入る）
 * - その他のクライアントのエラー（重複）: value の番号だけを数える（行内重複はカンマ区切り）。
 *   メッセージには行番号や文言の数字が入るため解析しない
 * - サーバーのエラー: value の番号に加え、メッセージ中の括弧（全角・半角）で囲まれた番号を数える。
 *   件数・上限値などの数字を拾わないよう、括弧の外は見ない
 *
 * @param {Array<object>} errors - clientErrors と serverErrors を合わせたもの
 * @returns {number}
 */
export function countExcludedPhones(errors) {
  const excludedPhones = new Set();
  const addValidPhones = (value) => {
    String(value).split(/,\s*/).filter(isValidExternalNumber).forEach(phone => excludedPhones.add(phone));
  };
  (errors || []).forEach((error) => {
    if (!error) return;
    if (error.source === 'client') {
      if (error.errorType === 'invalid_format') {
        if (error.value) excludedPhones.add(String(error.value));
        return;
      }
      if (error.errorType === 'missing_name' || error.errorType === 'missing_phone') return;
      if (error.value) addValidPhones(error.value);
      return;
    }
    if (error.value) addValidPhones(error.value);
    if (error.message) {
      const bracketed = String(error.message).match(/[（(]\s*\d{3,128}\s*[）)]/g) || [];
      bracketed.map(m => m.replace(/\D/g, '')).filter(isValidExternalNumber).forEach(phone => excludedPhones.add(phone));
    }
  });
  return excludedPhones.size;
}

// 1回のインポートで処理できる最大件数
// ※ サーバー側 server/app/controllers/PhoneBook.js の MAX_IMPORT_COUNT と同値。変更時は両方更新すること
const MAX_IMPORT_COUNT = 500;

// 変更してもプレビュー（import_preview_data / import_errors）が変わらない設定。
// 「登録済みデータとの重複」はサーバー側の判定なので、選び直しても解析結果は同じ。
// 再解析する側ではなくしない側を挙げているのは、解析結果を変える設定を後から
// 足したときに、書き足し忘れても再解析が走る（安全側に倒れる）ようにするため
const NO_RE_PARSE_SETTING_FIELDS = [
  'import_existing_duplicate_handling',
];

/**
 * 電話帳 CSVインポートモーダル
 */
export default class PhoneBookImportModal extends Component {
  constructor(props) {
    super(props);
    this.csvReaderRef = React.createRef();
    this.state = {
      import_csv_file: null,
      import_csv_filename: '',
      import_add_leading_zero: true,
      import_invalid_number_handling: 'exclude',
      import_file_duplicate_handling: 'exclude',
      // 【Anywhere #284】登録済みデータとの重複。exclude=①-a / overwrite=①-c / abort
      import_existing_duplicate_handling: 'exclude',
      import_preview_data: [],
      import_errors: [],
      // 【#415】結果に出す件数は単位ごとに分けて保持する。
      // ・成功行数 / 登録失敗行数 … サーバー（Anywhere）が返す行単位の集計
      // ・取り込み対象外行数 … サーバーへ送らずにクライアント側で弾いた行数
      // ・除外番号数 … エラーに現れた電話番号の件数（従来からある番号単位の値）
      import_parsed_row_count: 0,
      import_success_count: 0,
      import_server_failed_count: 0,
      import_skipped_row_count: 0,
      import_excluded_phone_count: 0,
      import_processing: false,
      import_show_result: false,
      import_result_details: [],
      import_is_complete_failure: false,  // すべて中止モードでの完全失敗フラグ
      csv_template_headers: [
        { label: '名前', key: 'name' },
        { label: 'メモ', key: 'memo' },
        { label: '電話番号1', key: 'phone1' },
        { label: '電話番号2', key: 'phone2' },
        { label: '電話番号3', key: 'phone3' },
      ],
    };
  }

  /**
   * CSVファイルドロップハンドラ
   */
  handleOnDrop = (data, file) => {
    if (!file) return;

    // ファイルサイズ制限
    const MAX_FILE_SIZE = 3 * 1024 * 1024; // 3MB
    if (file.size > MAX_FILE_SIZE) {
      this.showErrorObjectMessage("CsvFileTooLarge");
      this.removeCsvFile();
      return;
    }

    // ファイル形式チェック
    if (!file.name.endsWith('.csv')) {
      this.showErrorObjectMessage("CsvInvalidFileFormat");
      this.removeCsvFile();
      return;
    }

    this.setState({
      import_csv_file: file,
      import_csv_filename: file.name,
      import_errors: [],
      import_preview_data: [],
    }, () => {
      this.parseCSVFile(file);
    });
  }

  /**
   * CSVファイル削除ハンドラ
   */
  removeCsvFile = () => {
    this.setState({
      import_csv_file: null,
      import_csv_filename: '',
      import_errors: [],
      import_preview_data: [],
    });
  }

  /**
   * モーダルを閉じる時の状態初期化
   */
  resetModalState = () => {
    this.setState({
      import_csv_file: null,
      import_csv_filename: '',
      import_add_leading_zero: true,
      import_invalid_number_handling: 'exclude',
      import_file_duplicate_handling: 'exclude',
      // 【Anywhere #284】登録済みデータとの重複。exclude=①-a / overwrite=①-c / abort
      import_existing_duplicate_handling: 'exclude',
      import_preview_data: [],
      import_errors: [],
      import_parsed_row_count: 0,
      import_success_count: 0,
      import_server_failed_count: 0,
      import_skipped_row_count: 0,
      import_excluded_phone_count: 0,
      import_processing: false,
      import_show_result: false,
      import_result_details: [],
      import_is_complete_failure: false,
    });
  }

  /**
   * CSVファイルを解析
   */
  parseCSVFile = (file) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const lines = text.split(/\r?\n/).filter(line => line.trim());

        if (lines.length < 2) {
          this.showErrorObjectMessage("CsvInvalidStructure");
          return;
        }

        // ヘッダー行をチェック
        const header = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
        const nameIndex = header.findIndex(h => h === '名前' || h.toLowerCase() === 'name');
        const memoIndex = header.findIndex(h => h === 'メモ' || h.toLowerCase() === 'memo');

        // 電話番号列を全て見つける
        const phoneIndices = [];
        header.forEach((h, idx) => {
          if (h.match(/^電話番号\d+$/) || h.match(/^phone.*\d+$/i)) {
            phoneIndices.push(idx);
          }
        });

        if (nameIndex === -1 || phoneIndices.length === 0) {
          this.showErrorObjectMessage("CsvMissingRequiredColumn");
          return;
        }

        // データ行を解析
        let previewData = [];
        const errors = [];
        // 【#415】取り込み対象外になった行数を「解析した行数 - 送信した行数」で
        // 求めるため、空行を除いたデータ行の数を数えておく
        let parsedRowCount = 0;

        for (let i = 1; i < lines.length; i++) {
          const line = lines[i];
          if (!line.trim()) continue;
          parsedRowCount++;

          const cells = this.parseCSVLine(line);

          const name = nameIndex !== -1 && cells[nameIndex] ? cells[nameIndex].trim() : '';
          const memo = memoIndex !== -1 && cells[memoIndex] ? cells[memoIndex].trim() : '';
          const phoneNumbers = [];

          // 全ての電話番号列を取得
          phoneIndices.forEach((phoneIdx) => {
            if (cells[phoneIdx] && cells[phoneIdx].trim()) {
              phoneNumbers.push(cells[phoneIdx].trim());
            }
          });

          // 名前と電話番号のバリデーション
          if (!name) {
            errors.push({
              row: i + 1,
              value: '',
              message: `${i + 1}行目: 名前が入力されていません`,
              type: 'missing_name',
            });
            continue;
          }

          if (phoneNumbers.length === 0) {
            errors.push({
              row: i + 1,
              value: name,
              message: `${i + 1}行目: ${name} - 電話番号が入力されていません`,
              type: 'missing_phone',
            });
            continue;
          }

          // 電話番号の形式チェック
          const validPhoneNumbers = [];
          const invalidPhoneNumbers = [];

          phoneNumbers.forEach((phone) => {
            let processedPhone = phone.replace(/[-\s()]/g, '');

            // 先頭0の補正: 補正後に10〜11桁になる場合（元が9〜10桁）のみ付与する。
            // 3〜128桁へ緩和したことで、3桁の番号にも補正が働くのを防ぐ。
            if (
              this.state.import_add_leading_zero &&
              processedPhone.length > 0 &&
              processedPhone[0] !== '0' &&
              (processedPhone.length === 9 || processedPhone.length === 10)
            ) {
              processedPhone = '0' + processedPhone;
            }

            // 電話番号の形式チェック: 手動登録と同じ「数字のみ・3〜128桁」で判定する
            if (isValidExternalNumber(processedPhone)) {
              validPhoneNumbers.push(processedPhone);
            } else {
              invalidPhoneNumbers.push(phone);
            }
          });

          if (invalidPhoneNumbers.length > 0) {
            // 不正な電話番号がある場合、エラーメッセージを追加（モードに関わらず）
            invalidPhoneNumbers.forEach((invalidPhone) => {
              errors.push({
                row: i + 1,
                value: invalidPhone,
                message: `${i + 1}行目: ${invalidPhone} - ${this.props.langText.Body.PhoneBookImportResultErrorInvalidFormat}`,
                type: 'invalid_format', // エラー種別
                mode: this.state.import_invalid_number_handling, // モード情報を保存
              });
            });

            // 中止モードの場合は、この行をスキップ
            if (this.state.import_invalid_number_handling === 'abort') {
              continue;
            }
            // 除外モードの場合は、有効な番号のみで継続
          }

          if (validPhoneNumbers.length > 0) {
            // 同じ行内の重複を検出
            const uniquePhoneNumbers = [...new Set(validPhoneNumbers)];
            const hasDuplicatesInRow = validPhoneNumbers.length !== uniquePhoneNumbers.length;

            // 同じ行内に重複がある場合、メッセージを追加
            if (hasDuplicatesInRow) {
              const duplicatesInRow = validPhoneNumbers.filter((phone, index) =>
                validPhoneNumbers.indexOf(phone) !== index
              );
              const uniqueDuplicates = [...new Set(duplicatesInRow)];

              // 重複データ設定に応じてモードを決定
              const duplicateMode = this.state.import_file_duplicate_handling;
              const message = duplicateMode === 'abort'
                ? `${i + 1}行目: 同じ行内で重複している番号があります（${uniqueDuplicates.join(', ')}）`
                : `${i + 1}行目: 同じ行内で重複している番号があります（${uniqueDuplicates.join(', ')}）。重複は1つに統合されます。`;

              errors.push({
                row: i + 1,
                value: uniqueDuplicates.join(', '),
                message: message,
                type: 'duplicate_in_row', // 同じ行内の重複
                mode: duplicateMode, // 設定に応じて 'abort' または 'exclude'
              });

              // 中止モードの場合、この行をスキップ
              if (duplicateMode === 'abort') {
                continue;
              }
            }

            previewData.push({
              name: name,
              memo: memo,
              phoneNumbers: uniquePhoneNumbers,
              row: i + 1,
            });
          }
        }

        // CSV内重複チェック（異なる行間での重複のみを検出）
        const duplicates = this.checkDuplicates(previewData);
        if (duplicates.length > 0) {
          if (this.state.import_file_duplicate_handling === 'abort') {
            // 中止モード：エラーとして追加
            duplicates.forEach((dup) => {
              errors.push({
                row: dup.row,
                value: dup.phone,
                message: `${dup.row}行目: ${dup.phone} - ${this.props.langText.Body.PhoneBookImportResultErrorDuplicate}`,
                type: 'duplicate', // エラー種別
                mode: 'abort',
              });
            });
          } else if (this.state.import_file_duplicate_handling === 'exclude') {
            // 除外モード：2回目以降の重複番号のみを除外（行全体ではなく番号単位で除外）
            const duplicatePhonesByRow = new Map(); // row -> Set of duplicate phones

            duplicates.forEach((dup) => {
              if (!duplicatePhonesByRow.has(dup.row)) {
                duplicatePhonesByRow.set(dup.row, new Set());
              }
              duplicatePhonesByRow.get(dup.row).add(dup.phone);
            });

            const filteredPreviewData = [];
            previewData.forEach((item) => {
              // CSV内で重複している番号を除外
              const nonDuplicatePhones = item.phoneNumbers.filter((phone) => {
                return !(duplicatePhonesByRow.has(item.row) && duplicatePhonesByRow.get(item.row).has(phone));
              });

              if (nonDuplicatePhones.length > 0) {
                filteredPreviewData.push({
                  ...item,
                  phoneNumbers: nonDuplicatePhones,
                });
              }
            });

            // エラーメッセージを追加（除外した番号を通知）
            duplicates.forEach((dup) => {
              errors.push({
                row: dup.row,
                value: dup.phone,
                message: `${dup.row}行目: ${dup.phone} - CSV内で${dup.firstRow}行目と重複しているため除外されました`,
                type: 'duplicate', // エラー種別
                mode: 'exclude',
              });
            });

            // フィルタリング後のデータで上書き
            previewData = filteredPreviewData;
          }
        }

        this.setState({
          import_preview_data: previewData,
          import_errors: errors,
          // 【#415】結果表示で「取り込み対象外行数」を出すために保持する
          import_parsed_row_count: parsedRowCount,
        });
      } catch (error) {
        this.showErrorObjectMessage("CsvInvalidStructure");
      }
    };

    reader.onerror = () => {
      this.showErrorObjectMessage("CsvInvalidFileFormat");
    };

    // 文字コードの自動判定を試みる
    reader.readAsText(file, 'UTF-8');
  }

  /**
   * CSV行を解析（カンマ区切り、ダブルクォート対応）
   */
  parseCSVLine = (line) => {
    const cells = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];

      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        cells.push(current);
        current = '';
      } else {
        current += char;
      }
    }

    cells.push(current);
    return cells;
  }

  /**
   * 重複チェック（CSV内での異なる行間での重複を検出）
   * 最初の出現を除き、2回目以降の出現を重複として扱う
   * 前提：dataは既に各行内の重複が除去されている
   */
  checkDuplicates = (data) => {
    const phoneMap = new Map(); // phone -> {name, firstRow, duplicateRows}
    const duplicates = [];

    // 異なる行間での重複を検出
    data.forEach((item) => {
      item.phoneNumbers.forEach((phone) => {
        if (phoneMap.has(phone)) {
          // 2回目以降の出現（異なる行での重複）：重複として記録
          const existing = phoneMap.get(phone);
          existing.duplicateRows.push(item.row);
        } else {
          // 最初の出現：記録のみ
          phoneMap.set(phone, {
            name: item.name,
            firstRow: item.row,
            duplicateRows: [],
          });
        }
      });
    });

    // 2回目以降の出現のみを重複として返す
    phoneMap.forEach((value, phone) => {
      value.duplicateRows.forEach((row) => {
        duplicates.push({
          row: row,
          phone: phone,
          name: value.name,
          firstRow: value.firstRow, // 最初に出現した行番号も記録
        });
      });
    });

    return duplicates;
  }

  /**
   * 【Anywhere #284】「上書きして投入」を選べるか。
   *
   * 上書きは既存の連絡先から電話番号を剥がし、残り 0 件になった連絡先を削除するので、
   * 要約詳細の編集・削除と同じ権限を要求する。正はサーバー側
   * （server/app/controllers/PhoneBook.js の /import）のゲートで、
   * ここはその権限が無い利用者に選ばせないための補助。
   */
  canOverwriteExisting = () => {
    const permission = this.props.currentPermission || {};
    return !!(permission.update && permission.delete);
  }

  /**
   * ラジオボタン変更ハンドラ
   */
  handleRadioChange = (fieldName, value) => () => {
    this.setState({ [fieldName]: value }, () => {
      // 解析結果を変える設定のときだけCSVを再解析する。
      // 【Anywhere #284】「登録済みデータとの重複」はサーバー側で判定するため、
      // 選び直してもプレビューもクライアント側エラーも変わらない
      if (this.state.import_csv_file && !NO_RE_PARSE_SETTING_FIELDS.includes(fieldName)) {
        this.parseCSVFile(this.state.import_csv_file);
      }
    });
  }

  /**
   * インポート実行
   */
  handleImport = async () => {
    try {
      const { import_preview_data, import_errors } = this.state;

      // 中止モード（abort）のエラーがある場合は実行しない
      const hasAbortErrors = import_errors.some(e => e.mode === 'abort');
      if (hasAbortErrors) {
        // abort モードのエラー種別を判定
        const hasInvalidFormatAbort = import_errors.some(e => e.type === 'invalid_format' && e.mode === 'abort');
        const hasDuplicateAbort = import_errors.some(e => e.type === 'duplicate' && e.mode === 'abort');

        if (hasInvalidFormatAbort) {
          this.showErrorObjectMessage("PhoneBookImportAbortInvalidFormat");
        } else if (hasDuplicateAbort) {
          this.showErrorObjectMessage("PhoneBookImportAbortDuplicate");
        }
        return;
      }

      // プレビューデータもエラーもない場合は実行しない
      if (import_preview_data.length === 0 && import_errors.length === 0) {
        this.showErrorObjectMessage("CsvNoDataSelected");
        return;
      }

      // 件数上限チェック（タイムアウト防止）
      // ※ ボタンのdisabled制御とインライン警告で事前にブロックされるため、通常このパスには到達しない
      if (import_preview_data.length > MAX_IMPORT_COUNT) {
        this.showErrorObjectMessage("IMPORT_LIMIT_EXCEEDED");
        return;
      }

      // 除外モードで警告のみの場合は、プレビューデータが空でも実行可能

      this.setState({ import_processing: true });

      // Anywhereサーバーにインポートリクエストを送信
      const response = await this.ascAxios('post', 'PhoneBook/import', {
        data: import_preview_data,
        settings: {
          invalid_number_handling: this.state.import_invalid_number_handling,
          file_duplicate_handling: this.state.import_file_duplicate_handling,
          // 【Anywhere #284】file_duplicate_handling はフロントでは「ファイル内重複」、
          // サーバーでは「登録済みデータとの重複」に使われていて意味が混線していた。
          // 後者は専用のキーで送る（サーバーは未指定なら従来どおり後方互換で解決する）
          existing_duplicate_handling: this.state.import_existing_duplicate_handling,
        }
      });

      if (response && response.data) {
        // 【#415】failed_count はサーバー（Anywhere）が数えた行単位の失敗件数。
        // 従来は受け取っていたが参照しておらず、画面には電話番号から数えた
        // 件数だけが出ていた（#384 で未使用の変数として削除した箇所）
        const { success_count, failed_count, errors, warnings } = response.data;

        // すべて中止モードで全処理が失敗した場合のチェック
        const isAbortMode = (
          this.state.import_file_duplicate_handling === 'abort'
          || this.state.import_invalid_number_handling === 'abort'
          || this.state.import_existing_duplicate_handling === 'abort'
        );
        const isCompleteFailure = (isAbortMode && success_count === 0 && (errors && errors.length > 0));

        // クライアント側で検出したエラーを整形
        const clientErrors = [];
        if (import_errors && import_errors.length > 0) {
          import_errors.forEach((error) => {
            clientErrors.push({
              row: error.row,
              value: error.value || '',
              message: error.message,
              type: 'error',
              // 【#607】除外番号数の集計で、エラーの種別と出どころを見分けるために残す
              errorType: error.type,
              source: 'client'
            });
          });
        }

        // サーバー側から返ってきたエラーを整形
        const serverErrors = [];
        if (errors && Array.isArray(errors) && errors.length > 0) {
          errors.forEach((error) => {
            if (error.row && error.message) {
              serverErrors.push({
                row: error.row,
                value: error.value || '',
                message: error.message,
                type: 'error',
                source: 'server'
              });
            } else if (typeof error === 'string') {
              // エラーが文字列の場合
              serverErrors.push({
                message: error,
                type: 'error',
                source: 'server'
              });
            }
          });
        }

        // サーバー側から返ってきた警告を整形
        const serverWarnings = [];
        if (warnings && Array.isArray(warnings) && warnings.length > 0) {
          warnings.forEach((warning) => {
            if (warning.row && warning.message) {
              serverWarnings.push({
                row: warning.row,
                value: warning.value || '',
                message: warning.message,
                type: 'warning'
              });
            } else if (typeof warning === 'string') {
              serverWarnings.push({
                message: warning,
                type: 'warning'
              });
            }
          });
        }

        // クライアント側とサーバー側のエラーと警告を統合
        const allErrors = [...clientErrors, ...serverErrors];
        const allMessages = [...clientErrors, ...serverErrors, ...serverWarnings];

        // 行番号でメッセージをグループ化（エラーと警告を両方含む）
        const messagesByRow = new Map();
        allMessages.forEach((msg) => {
          if (msg.row) {
            if (!messagesByRow.has(msg.row)) {
              messagesByRow.set(msg.row, []);
            }
            messagesByRow.get(msg.row).push(msg.message);
          } else {
            // 行番号がないメッセージは別途追加
            if (!messagesByRow.has('other')) {
              messagesByRow.set('other', []);
            }
            messagesByRow.get('other').push(msg.message);
          }
        });

        // 行番号順にソートして表示用のリストを作成
        const groupedMessages = [];
        const sortedRows = Array.from(messagesByRow.keys()).sort((a, b) => {
          if (a === 'other') return 1;
          if (b === 'other') return -1;
          return a - b;
        });

        sortedRows.forEach((row) => {
          const messages = messagesByRow.get(row);
          if (row === 'other') {
            // 行番号がないメッセージはそのまま追加
            messages.forEach((msg) => groupedMessages.push({ message: msg }));
          } else {
            // 同じ行のメッセージを結合
            groupedMessages.push({
              row: row,
              message: `${row}行目:\n${messages.map(m => {
                // "X行目: " のプレフィックスを削除
                const cleanMsg = m.replace(/^\d+行目:\s*/, '');
                return `  - ${cleanMsg}`;
              }).join('\n')}`
            });
          }
        });

        // 除外された電話番号の数を計算（エラーのみ、警告は含まない）
        const excludedPhoneCount = countExcludedPhones(allErrors);

        // すべて中止モードで全処理が失敗した場合は、メッセージの先頭に警告を追加
        if (isCompleteFailure) {
          groupedMessages.unshift({
            message: '⚠️ すべて中止モードのため、エラーが検出され全ての登録処理が中止されました。'
          });
        }

        // 【#415】クライアント側で取り込み対象外にした行数。
        // エラーの件数ではなく「解析した行数 - サーバーへ送った行数」で求める。
        // 1行に複数のエラーが付く場合や、番号単位のエラーが混ざる場合があり、
        // エラー配列の件数は行数と一致しないため。
        // 名前が空・電話番号が無い・中止モードの重複でスキップ・重複除外で
        // 番号が0件になった、のすべてがこの差分に含まれる。
        const skippedRowCount = Math.max(
          0, this.state.import_parsed_row_count - import_preview_data.length
        );

        this.setState({
          import_success_count: success_count || 0,
          // サーバーが登録に失敗した行数。すべて中止モードでは送った全行が失敗として返る
          import_server_failed_count: Number(failed_count) || 0,
          import_skipped_row_count: skippedRowCount,
          // 従来の「除外番号数」。単位が番号のため行数とは別の欄に出す
          import_excluded_phone_count: excludedPhoneCount,
          import_result_details: groupedMessages,
          import_processing: false,
          import_is_complete_failure: isCompleteFailure,  // 完全失敗フラグを保存
        });

        // データを再読み込み（成功時も失敗時も実行）
        if (this.props.onImportComplete) {
          await this.props.onImportComplete();
        }

        // エラーまたは警告がある場合は結果詳細モーダルを表示
        // 【#415】判定はメッセージの有無で行う。従来は電話番号から数えた件数が
        // 0 かどうかで判定しており、名前が空の行のように電話番号を含まない
        // エラーだけが出たときは明細を出さずに閉じていたため、
        // 取り込めなかった行が利用者に通知されなかった。
        if (allErrors.length === 0 && serverWarnings.length === 0) {
          // 完全成功（エラーも警告もなし）
          alert(this.props.langText.Body.PhoneBookImportSuccess.replace('%d', success_count));
          this.resetModalState();
          this.props.propSetState({ show: false });
        } else {
          // エラーまたは警告がある場合は結果詳細モーダルを表示
          this.setState({ import_show_result: true });
        }
      }
    } catch (error) {
      this.setState({ import_processing: false });
      this.showErrorObjectMessage(error, "PhoneBookImportError");
    }
  }


  /**
   * 結果詳細をクリップボードにコピー
   */
  copyResultDetails = () => {
    const {
      import_success_count,
      import_server_failed_count,
      import_skipped_row_count,
      import_excluded_phone_count,
      import_result_details,
    } = this.state;
    const { langText } = this.props;
    const text = `${langText.Body.PhoneBookImportResultMessage}\n\n` +
                 `${langText.Body.PhoneBookImportResultSuccessCount.replace('%d', import_success_count)}\n` +
                 `${langText.Body.PhoneBookImportResultServerFailedCount.replace('%d', import_server_failed_count)}\n` +
                 `${langText.Body.PhoneBookImportResultSkippedRowCount.replace('%d', import_skipped_row_count)}\n` +
                 `${langText.Body.PhoneBookImportResultFailedCount.replace('%d', import_excluded_phone_count)}\n\n` +
                 import_result_details.map(err => err.message).join('\n');

    navigator.clipboard.writeText(text).then(() => {
      alert(this.props.langText.Body.CopiedToClipboard);
    });
  }

  render() {
    const { langText } = this.props;
    const {
      import_csv_filename,
      import_add_leading_zero,
      import_invalid_number_handling,
      import_file_duplicate_handling,
      import_existing_duplicate_handling,
      import_preview_data,
      import_errors,
      import_processing,
      import_show_result,
      import_success_count,
      import_server_failed_count,
      import_skipped_row_count,
      import_excluded_phone_count,
      import_result_details,
      import_is_complete_failure,
    } = this.state;

    const canOverwriteExisting = this.canOverwriteExisting();

    // 結果詳細モーダル
    if (import_show_result) {
      return (
        <Modal
          show={this.props.state.show}
          onHide={() => {
            this.resetModalState();
            this.props.propSetState({ show: false });
            if (this.props.onImportComplete) {
              this.props.onImportComplete();
            }
          }}
          size="lg"
          centered
          scrollable={true}
        >
          <Modal.Header>
            <Modal.Title>
              <Row>
                <Col className="modal-title-text" lg={11} sm={11} xs={11}>
                  {langText.Body.PhoneBookImportResultTitle}
                </Col>
                <Col lg={1} sm={1} xs={1}>
                  <Button
                    className="x-execute"
                    onClick={() => {
                      this.resetModalState();
                      this.props.propSetState({ show: false });
                      if (this.props.onImportComplete) {
                        this.props.onImportComplete();
                      }
                    }}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </Button>
                </Col>
              </Row>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Card variant="info">
              <Card.Header>
                <Card.Title>{langText.Body.ImportResult}</Card.Title>
              </Card.Header>
              <Card.Body className="card-body-centered">
                <Alert variant={import_is_complete_failure ? "danger" : "warning"}>
                  {import_is_complete_failure ? (
                    <>
                      <p><strong><span role="img" aria-label="警告">⚠️</span> 全ての登録処理が中止されました</strong></p>
                      <p>{langText.Body.PhoneBookImportResultMessage}</p>
                    </>
                  ) : (
                    <p>{langText.Body.PhoneBookImportResultMessage}</p>
                  )}
                  {/* 【#415】行単位の件数（サーバー由来）と番号単位の件数を分けて出す。
                      成功行数 + 登録失敗行数 + 取り込み対象外行数 = CSVのデータ行数 */}
                  <p><strong>{langText.Body.PhoneBookImportResultSuccessCount.replace('%d', import_success_count)}</strong></p>
                  <p><strong>{langText.Body.PhoneBookImportResultServerFailedCount.replace('%d', import_server_failed_count)}</strong></p>
                  <p><strong>{langText.Body.PhoneBookImportResultSkippedRowCount.replace('%d', import_skipped_row_count)}</strong></p>
                  <p><strong>{langText.Body.PhoneBookImportResultFailedCount.replace('%d', import_excluded_phone_count)}</strong></p>
                </Alert>
                <div style={{ maxHeight: '400px', overflowY: 'auto', whiteSpace: 'pre-wrap' }}>
                  {import_result_details.map((err, idx) => (
                    <div key={idx} style={{ marginBottom: '10px' }}>
                      {err.message}
                    </div>
                  ))}
                </div>
              </Card.Body>
            </Card>
          </Modal.Body>
          <Modal.Footer>
            <Button
              className="cancel-execute"
              variant="secondary"
              onClick={this.copyResultDetails}
            >
              {langText.Body.PhoneBookImportResultCopyButton}
            </Button>
            <Button
              className="ok-execute"
              variant="primary"
              onClick={() => {
                this.resetModalState();
                this.props.propSetState({ show: false });
                if (this.props.onImportComplete) {
                  this.props.onImportComplete();
                }
              }}
            >
              {langText.Body.PhoneBookImportResultCloseButton}
            </Button>
          </Modal.Footer>
        </Modal>
      );
    }

    // インポートモーダル
    return (
      <Modal
        show={this.props.state.show}
        onHide={() => {
          this.resetModalState();
          this.props.propSetState({ show: false });
        }}
        size="lg"
        centered
        scrollable={true}
      >
        <Modal.Header>
          <Modal.Title>
            <Row>
              <Col className="modal-title-text" lg={11} sm={11} xs={11}>
                {langText.Body.PhoneBookImportTitle}
              </Col>
              <Col lg={1} sm={1} xs={1}>
                <Button
                  className="x-execute"
                  onClick={() => {
                    this.resetModalState();
                    this.props.propSetState({ show: false });
                  }}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </Button>
              </Col>
            </Row>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Card variant="info">
            <Card.Header>
              <Card.Title>
                {langText.Body.ImportFileSelect}
                {' '}
                <OverlayTrigger
                  placement="bottom"
                  overlay={
                    <Tooltip id="file-select-tooltip">
                      <div style={{ whiteSpace: 'pre-wrap' }}>
                        {langText.Body.ImportFileSelectTooltip}
                      </div>
                    </Tooltip>
                  }
                  delayShow={300}
                >
                  <FontAwesomeIcon
                    icon={faInfoCircle}
                    style={{ cursor: 'pointer', color: '#6c757d' }}
                  />
                </OverlayTrigger>
              </Card.Title>
            </Card.Header>
            <Card.Body className="card-body-centered">
              <div className="mb-3">
                <CSVReader
                  ref={this.csvReaderRef}
                  onDrop={this.handleOnDrop}
                  onError={this.removeCsvFile}
                  addRemoveButton={!import_processing}
                  noClick={import_processing}
                  removeButtonColor="#efa865ff"
                  onRemoveFile={this.removeCsvFile}
                  config={{
                    encoding: "UTF-8"
                  }}
                  style={{
                    dropArea: {
                      borderColor: '#7e7e7eff',
                      borderRadius: '8px',
                      minHeight: '120px',
                    },
                    dropAreaActive: {
                      borderColor: '#b36b00ff',
                    },
                    dropFile: {
                      width: '100%',
                      height: '80px',
                      background: '#f8f9fa',
                    },
                    fileSizeInfo: {
                      color: '#7d746cff',
                      lineHeight: 1.2,
                      marginTop: '8px',
                    },
                    fileNameInfo: {
                      color: '#495057',
                      fontWeight: 'bold',
                      marginTop: '8px',
                    },
                    removeButton: {
                      color: '#efa865ff',
                    },
                    progressBar: {
                      backgroundColor: '#ffa600ff',
                    },
                  }}
                >
                  <span style={{ fontSize: '14px', color: '#6c757d' }}>
                    {langText.Body.ImportFileSelectDragDrop}
                  </span>
                </CSVReader>
                {import_csv_filename && (
                  <Form.Text className="file-selected-text d-block mt-2">
                    {langText.Body.ImportFileSelected}: {import_csv_filename}
                  </Form.Text>
                )}
              </div>

              <div className="mb-3">
                <CSVLink
                  headers={this.state.csv_template_headers}
                  data={[]}
                  filename={langText.Body.ImportTemplateFileName}
                  className="btn btn-link btn-sm"
                  style={{ textDecoration: 'none', padding: 0 }}
                >
                  <strong>{langText.Body.ImportTemplateDownload}</strong>
                </CSVLink>
                <Form.Text className="text-muted d-block mt-2">
                  {langText.Body.ImportTemplateRequiredHeaders}
                </Form.Text>
                <Form.Text className="text-muted d-block">
                  {langText.Body.ImportFileFormat}
                </Form.Text>
                <Form.Text className="text-muted d-block">
                  {langText.Body.ImportFileMultiplePhoneNote}
                </Form.Text>
              </div>
            </Card.Body>
          </Card>

          <Card variant="info" className="mt-3">
            <Card.Header>
              <Card.Title>{langText.Body.ImportSettings}</Card.Title>
            </Card.Header>
            <Card.Body className="card-body-centered">
              <Form.Group className="mb-3">
                <Form.Label className="mb-2"><strong>{langText.Body.ImportPhoneNumberLeadingZero}</strong></Form.Label>
                <div style={{ paddingLeft: '10px' }}>
                  <Form.Check
                    type="radio"
                    label={langText.Body.ImportPhoneNumberAddZero}
                    checked={import_add_leading_zero === true}
                    onChange={this.handleRadioChange('import_add_leading_zero', true)}
                    className="mb-2"
                  />
                  <Form.Check
                    type="radio"
                    label={langText.Body.ImportPhoneNumberNoChange}
                    checked={import_add_leading_zero === false}
                    onChange={this.handleRadioChange('import_add_leading_zero', false)}
                    className="mb-2"
                  />
                </div>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="mb-2"><strong>{langText.Body.ImportInvalidNumberHandling}</strong></Form.Label>
                <div style={{ paddingLeft: '10px' }}>
                  <Form.Check
                    type="radio"
                    label={langText.Body.ImportInvalidNumberExclude}
                    checked={import_invalid_number_handling === 'exclude'}
                    onChange={this.handleRadioChange('import_invalid_number_handling', 'exclude')}
                    className="mb-2"
                  />
                  <Form.Check
                    type="radio"
                    label={langText.Body.ImportInvalidNumberAbort}
                    checked={import_invalid_number_handling === 'abort'}
                    onChange={this.handleRadioChange('import_invalid_number_handling', 'abort')}
                    className="mb-2"
                  />
                </div>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="mb-2">
                  <strong>{langText.Body.ImportFileDuplicate}</strong>
                  {' '}
                  <OverlayTrigger
                    placement="bottom"
                    overlay={
                      <Tooltip id="duplicate-tooltip">
                        <div style={{ whiteSpace: 'pre-wrap' }}>
                          {langText.Body.ImportFileDuplicateTooltip}
                        </div>
                      </Tooltip>
                    }
                    delayShow={300}
                  >
                    <FontAwesomeIcon
                      icon={faInfoCircle}
                      style={{ cursor: 'pointer', color: '#6c757d' }}
                    />
                  </OverlayTrigger>
                </Form.Label>
                <div style={{ paddingLeft: '10px' }}>
                  <Form.Check
                    type="radio"
                    label={langText.Body.ImportFileDuplicateExclude}
                    checked={import_file_duplicate_handling === 'exclude'}
                    onChange={this.handleRadioChange('import_file_duplicate_handling', 'exclude')}
                    className="mb-2"
                  />
                  <Form.Check
                    type="radio"
                    label={langText.Body.ImportFileDuplicateAbort}
                    checked={import_file_duplicate_handling === 'abort'}
                    onChange={this.handleRadioChange('import_file_duplicate_handling', 'abort')}
                    className="mb-2"
                  />
                </div>
              </Form.Group>

              {/* 【Anywhere #284】登録済みの連絡先と電話番号が重複したときの扱い */}
              <Form.Group className="mb-3">
                <Form.Label className="mb-2">
                  <strong>{langText.Body.ImportExistingDuplicate}</strong>
                  {' '}
                  <OverlayTrigger
                    placement="bottom"
                    overlay={
                      <Tooltip id="existing-duplicate-tooltip">
                        <div style={{ whiteSpace: 'pre-wrap' }}>
                          {langText.Body.ImportExistingDuplicateTooltip}
                        </div>
                      </Tooltip>
                    }
                    delayShow={300}
                  >
                    <FontAwesomeIcon
                      icon={faInfoCircle}
                      style={{ cursor: 'pointer', color: '#6c757d' }}
                    />
                  </OverlayTrigger>
                </Form.Label>
                <div style={{ paddingLeft: '10px' }}>
                  <Form.Check
                    type="radio"
                    label={langText.Body.ImportExistingDuplicateExclude}
                    checked={import_existing_duplicate_handling === 'exclude'}
                    onChange={this.handleRadioChange('import_existing_duplicate_handling', 'exclude')}
                    className="mb-2"
                  />
                  <Form.Check
                    type="radio"
                    label={langText.Body.ImportExistingDuplicateOverwrite}
                    checked={import_existing_duplicate_handling === 'overwrite'}
                    onChange={this.handleRadioChange('import_existing_duplicate_handling', 'overwrite')}
                    disabled={!canOverwriteExisting}
                    className={canOverwriteExisting ? 'mb-2' : ''}
                  />
                  {!canOverwriteExisting && (
                    <Form.Text className="text-muted d-block mb-2">
                      {langText.Body.ImportExistingDuplicateOverwriteNoPermission}
                    </Form.Text>
                  )}
                  <Form.Check
                    type="radio"
                    label={langText.Body.ImportExistingDuplicateAbort}
                    checked={import_existing_duplicate_handling === 'abort'}
                    onChange={this.handleRadioChange('import_existing_duplicate_handling', 'abort')}
                    className="mb-2"
                  />
                </div>
              </Form.Group>
            </Card.Body>
          </Card>

          {/* エラー表示 */}
          {import_errors.length > 0 && (() => {
            // エラーを種別とモードで分類
            const abortErrors = import_errors.filter(e => e.mode === 'abort');
            const excludeWarnings = import_errors.filter(e => e.mode === 'exclude');

            // 中止モードのエラーがある場合は「danger」、なければ「warning」
            const hasAbortErrors = abortErrors.length > 0;
            const variant = hasAbortErrors ? 'danger' : 'warning';

            // タイトルを設定
            let title;
            if (hasAbortErrors && excludeWarnings.length > 0) {
              title = `エラー: ${abortErrors.length}件、警告: ${excludeWarnings.length}件`;
            } else if (hasAbortErrors) {
              title = langText.Body.ImportErrorCount.replace('%d', abortErrors.length);
            } else {
              title = `警告: ${excludeWarnings.length}件の不正なデータを検出（除外して登録されます）`;
            }

            return (
              <Alert variant={variant} className="mt-3">
                <strong>{title}</strong>
                <ul style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {(() => {
                    const MAX_DISPLAYED_ERRORS = 10;
                    return (
                      <>
                        {import_errors.slice(0, MAX_DISPLAYED_ERRORS).map((error, index) => (
                          <li key={index}>{error.message}</li>
                        ))}
                        {import_errors.length > MAX_DISPLAYED_ERRORS && <li>...他 {import_errors.length - MAX_DISPLAYED_ERRORS}件</li>}
                      </>
                    );
                  })()}
                </ul>
              </Alert>
            );
          })()}
          {/* 件数上限超過の警告 */}
          {import_preview_data.length > MAX_IMPORT_COUNT && (
            <Alert variant="danger" className="mt-3">
              {langText.Message.CsvImportLimitExceeded
                .replace('{0}', MAX_IMPORT_COUNT)
                .replace('{1}', import_preview_data.length)}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="cancel-execute"
            variant="outline-secondary"
            onClick={() => {
              this.resetModalState();
              this.props.propSetState({ show: false });
            }}
          >
            {langText.Body.Cancel}
          </Button>
          <Button
            className="ok-execute"
            variant="primary"
            onClick={this.handleImport}
            disabled={(() => {
              // 処理中の場合は無効化
              if (import_processing) return true;

              // 中止モード（abort）のエラーがある場合は無効化
              const hasAbortErrors = import_errors.some(e => e.mode === 'abort');
              if (hasAbortErrors) return true;

              // 件数上限超過の場合は無効化
              if (import_preview_data.length > MAX_IMPORT_COUNT) return true;

              // 除外モードの警告のみの場合は有効化
              // （プレビューデータが空でも、除外モードであれば実行可能）
              const hasExcludeWarnings = import_errors.some(e => e.mode === 'exclude');
              if (hasExcludeWarnings && import_preview_data.length === 0) {
                // 除外警告があり、プレビューデータが空の場合でも有効化
                return false;
              }

              // プレビューデータもエラーもない場合は無効化
              if (import_preview_data.length === 0 && import_errors.length === 0) return true;

              // それ以外の場合は有効化
              return false;
            })()}
          >
            {import_processing ? langText.Body.ImportProcessing : langText.Body.ImportExecute}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}
